import { useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import "./ChangeAccessCodePage.css";
import "./PinLoginPage.css";
import PinKeypad from "./PinKeypad";
import { useDoubleEntryPin } from "../../../hooks/useDoubleEntryPin";
import { authService } from "../../../services";
import { authUserSignal } from "../../../signals/authSignal";
import { useLanguage } from "../../../contexts/LanguageContext";
import { createLogger } from "../../../utils/logger";

const logger = createLogger("ChangeAccessCodePage");

interface ChangeAccessCodePageProps {
  mode?: "forced" | "voluntary";
}

type Step = "current-pin" | "new-pin" | "submitting";

// TODO(settings-page): once a settings/profile screen exists, link to
// /change-access-code?mode=voluntary from there instead of only reaching
// this page via the forced-login redirect.

/**
 * Container/orchestration component only: owns step sequencing, service
 * calls, and navigation. Digit-entry UI lives in PinKeypad, and the
 * "enter new PIN twice and compare" state lives in useDoubleEntryPin -
 * this component just wires them together and reacts to their results.
 */
const ChangeAccessCodePage = ({ mode = "forced" }: ChangeAccessCodePageProps) => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const effectiveMode = searchParams.get("mode") === "voluntary" ? "voluntary" : mode;

  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";

  const [step, setStep] = useState<Step>("current-pin");
  const [currentPin, setCurrentPin] = useState("");
  const [currentPinError, setCurrentPinError] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [showDigits, setShowDigits] = useState(false);

  const doubleEntry = useDoubleEntryPin();

  const handleCurrentPinDigit = (digit: string) => {
    setCurrentPinError(false);
    if (currentPin.length < 10) {
      setCurrentPin(currentPin + digit);
    }
  };

  const handleCurrentPinSubmit = () => {
    if (currentPin.length < 4) {
      setCurrentPinError(true);
      return;
    }
    setStep("new-pin");
  };

  const handleNewPinSubmit = async () => {
    const advanced = doubleEntry.advance();
    if (!advanced) {
      // Either too short, or (when in confirm-new) a mismatch - the hook
      // already surfaced the appropriate error/reset.
      return;
    }
    if (doubleEntry.stage === "enter-new") {
      // advance() just moved us from enter-new to confirm-new; wait for the
      // user to type the confirmation before submitting anything.
      return;
    }

    setStep("submitting");
    setServerError(null);

    const result = await authService.changeAccessCode(currentPin, doubleEntry.firstPin);

    if (result.success) {
      logger.debug("changeAccessCode succeeded");
      if (authUserSignal.value) {
        authUserSignal.value = { ...authUserSignal.value, mustChangePassword: false };
      }
      if (effectiveMode === "voluntary") {
        navigate(-1);
      } else {
        navigate(from, { replace: true });
      }
      return;
    }

    if (result.error === "INVALID_CURRENT_PIN") {
      setServerError(t("changeAccessCode.invalidCurrentPin"));
      setStep("current-pin");
      setCurrentPin("");
    } else if (result.error === "INVALID_NEW_PIN") {
      setServerError(t("changeAccessCode.invalidNewPin"));
      doubleEntry.reset();
      setStep("new-pin");
    } else {
      setServerError(t("changeAccessCode.genericError"));
      doubleEntry.reset();
      setStep("new-pin");
    }
  };

  const heading =
    effectiveMode === "forced"
      ? t("changeAccessCode.forcedHeading")
      : t("changeAccessCode.voluntaryHeading");

  return (
    <div className="container-fluid vh-100 d-flex justify-content-center align-items-center pin-login-container">
      <div className="pin-login-panel change-access-code-panel text-center p-4 rounded rounded-3 animate__animated animate__zoomIn">
        <h2 className="change-access-code-heading mb-3">{heading}</h2>

        {serverError && (
          <div className="change-access-code-error mb-3">{serverError}</div>
        )}

        {step === "current-pin" && (
          <>
            <p className="change-access-code-step-label">{t("changeAccessCode.currentPinLabel")}</p>
            <PinKeypad
              value={currentPin}
              onDigit={handleCurrentPinDigit}
              onBackspace={() => setCurrentPin(currentPin.slice(0, -1))}
              onSubmit={handleCurrentPinSubmit}
              showDigits={showDigits}
              onToggleShowDigits={() => setShowDigits(!showDigits)}
            />
            {currentPinError && (
              <div className="change-access-code-error mb-2">{t("changeAccessCode.minDigitsError")}</div>
            )}
            <div className="row row-pin mb-2 text-center">
              <button className="btn-theme btn-pin-login mx-2 mt-3 rounded" onClick={handleCurrentPinSubmit}>
                {t("changeAccessCode.continueButton")}
              </button>
            </div>
          </>
        )}

        {(step === "new-pin" || step === "submitting") && (
          <>
            <p className="change-access-code-step-label">
              {doubleEntry.stage === "enter-new" ? t("changeAccessCode.newPinLabel") : t("changeAccessCode.confirmPinLabel")}
            </p>
            <PinKeypad
              value={doubleEntry.pinValue}
              onDigit={doubleEntry.setDigit}
              onBackspace={doubleEntry.backspace}
              onSubmit={handleNewPinSubmit}
              showDigits={showDigits}
              onToggleShowDigits={() => setShowDigits(!showDigits)}
              disabled={step === "submitting"}
            />
            {doubleEntry.mismatchError && (
              <div className="change-access-code-error mb-2">{t("changeAccessCode.mismatchError")}</div>
            )}
            <div className="row row-pin mb-2 text-center">
              <button
                disabled={step === "submitting"}
                className="btn-theme btn-pin-login mx-2 mt-3 rounded"
                onClick={handleNewPinSubmit}
              >
                {step === "submitting"
                  ? t("changeAccessCode.saving")
                  : doubleEntry.stage === "enter-new"
                  ? t("changeAccessCode.continueButton")
                  : t("changeAccessCode.confirmButton")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChangeAccessCodePage;
